import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, ArrowLeft, Save, Building, Hash, Mail, Receipt, 
  CheckCircle, Clock, AlertCircle, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REGIMEN_FISCAL_OPTIONS = [
  { value: '601', label: '601 - General de Ley Personas Morales' },
  { value: '603', label: '603 - Personas Morales con Fines no Lucrativos' },
  { value: '605', label: '605 - Sueldos y Salarios e Ingresos Asimilados' },
  { value: '606', label: '606 - Arrendamiento' },
  { value: '607', label: '607 - Régimen de Enajenación o Adquisición de Bienes' },
  { value: '608', label: '608 - Demás ingresos' },
  { value: '610', label: '610 - Residentes en el Extranjero' },
  { value: '611', label: '611 - Ingresos por Dividendos' },
  { value: '612', label: '612 - Personas Físicas con Actividades Empresariales' },
  { value: '614', label: '614 - Ingresos por intereses' },
  { value: '615', label: '615 - Régimen de los ingresos por obtención de premios' },
  { value: '616', label: '616 - Sin obligaciones fiscales' },
  { value: '620', label: '620 - Sociedades Cooperativas de Producción' },
  { value: '621', label: '621 - Incorporación Fiscal' },
  { value: '622', label: '622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { value: '623', label: '623 - Opcional para Grupos de Sociedades' },
  { value: '624', label: '624 - Coordinados' },
  { value: '625', label: '625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
  { value: '626', label: '626 - Régimen Simplificado de Confianza' },
];

const USO_CFDI_OPTIONS = [
  { value: 'G01', label: 'G01 - Adquisición de mercancías' },
  { value: 'G02', label: 'G02 - Devoluciones, descuentos o bonificaciones' },
  { value: 'G03', label: 'G03 - Gastos en general' },
  { value: 'I01', label: 'I01 - Construcciones' },
  { value: 'I02', label: 'I02 - Mobiliario y equipo de oficina' },
  { value: 'I03', label: 'I03 - Equipo de transporte' },
  { value: 'I04', label: 'I04 - Equipo de cómputo y accesorios' },
  { value: 'D01', label: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
  { value: 'D02', label: 'D02 - Gastos médicos por incapacidad o discapacidad' },
  { value: 'D03', label: 'D03 - Gastos funerales' },
  { value: 'D04', label: 'D04 - Donativos' },
  { value: 'P01', label: 'P01 - Por definir' },
  { value: 'S01', label: 'S01 - Sin efectos fiscales' },
];

const statusConfig = {
  pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-700', label: 'Pendiente' },
  processing: { icon: Clock, color: 'bg-blue-100 text-blue-700', label: 'Procesando' },
  completed: { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700', label: 'Completada' },
  error: { icon: AlertCircle, color: 'bg-red-100 text-red-700', label: 'Error' },
};

export default function BillingSettings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [billingInfo, setBillingInfo] = useState(null);
  const [invoiceRequests, setInvoiceRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  
  const [form, setForm] = useState({
    rfc: '',
    razon_social: '',
    regimen_fiscal: '',
    codigo_postal: '',
    email_facturacion: '',
    uso_cfdi: 'G03'
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
      return;
    }
    
    const fetchData = async () => {
      try {
        const [billingRes, invoicesRes, ordersRes] = await Promise.all([
          fetch(`${API}/billing`, { credentials: 'include' }),
          fetch(`${API}/billing/invoices`, { credentials: 'include' }),
          fetch(`${API}/orders`, { credentials: 'include' })
        ]);

        if (billingRes.ok) {
          const data = await billingRes.json();
          if (data) {
            setBillingInfo(data);
            setForm({
              rfc: data.rfc || '',
              razon_social: data.razon_social || '',
              regimen_fiscal: data.regimen_fiscal || '',
              codigo_postal: data.codigo_postal || '',
              email_facturacion: data.email_facturacion || '',
              uso_cfdi: data.uso_cfdi || 'G03'
            });
          }
        }

        if (invoicesRes.ok) {
          setInvoiceRequests(await invoicesRes.json());
        }

        if (ordersRes.ok) {
          const ordersData = await ordersRes.json();
          setOrders(ordersData.filter(o => o.payment_status === 'paid'));
        }
      } catch (error) {
        console.error('Error fetching billing data:', error);
      }
    };

    if (user) fetchData();
  }, [user, authLoading, navigate]);

  const handleSaveBilling = async () => {
    if (!form.rfc || !form.razon_social || !form.regimen_fiscal || !form.codigo_postal || !form.email_facturacion) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/billing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });

      if (response.ok) {
        const data = await response.json();
        setBillingInfo(data);
        toast.success('Datos de facturación guardados');
      } else {
        throw new Error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestInvoice = async (orderId) => {
    try {
      const response = await fetch(`${API}/billing/request-invoice/${orderId}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Solicitud de factura enviada');
        // Refresh invoice requests
        const invoicesRes = await fetch(`${API}/billing/invoices`, { credentials: 'include' });
        if (invoicesRes.ok) {
          setInvoiceRequests(await invoicesRes.json());
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al solicitar factura');
      }
    } catch (error) {
      toast.error('Error al solicitar factura');
    }
  };

  const isInvoiceRequested = (orderId) => {
    return invoiceRequests.some(inv => inv.order_id === orderId);
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="billing-settings">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/settings')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-stone-900">Facturación</h1>
              <p className="text-stone-500">Configura tus datos fiscales y solicita facturas</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Billing Info Form */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-700" />
                </div>
                <h2 className="text-lg font-semibold text-stone-900">Datos Fiscales</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                    <Hash className="w-4 h-4" />
                    RFC
                  </label>
                  <Input
                    value={form.rfc}
                    onChange={(e) => setForm({...form, rfc: e.target.value.toUpperCase()})}
                    placeholder="XAXX010101000"
                    className="bg-stone-50 border-stone-200 rounded-xl h-12"
                    maxLength={13}
                    data-testid="billing-rfc"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                    <Building className="w-4 h-4" />
                    Razón Social
                  </label>
                  <Input
                    value={form.razon_social}
                    onChange={(e) => setForm({...form, razon_social: e.target.value})}
                    placeholder="Nombre o Razón Social"
                    className="bg-stone-50 border-stone-200 rounded-xl h-12"
                    data-testid="billing-razon-social"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-700 mb-2 block">Régimen Fiscal</label>
                  <Select
                    value={form.regimen_fiscal}
                    onValueChange={(value) => setForm({...form, regimen_fiscal: value})}
                  >
                    <SelectTrigger className="bg-stone-50 border-stone-200 rounded-xl h-12" data-testid="billing-regimen">
                      <SelectValue placeholder="Seleccionar régimen" />
                    </SelectTrigger>
                    <SelectContent>
                      {REGIMEN_FISCAL_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-700 mb-2 block">Código Postal</label>
                  <Input
                    value={form.codigo_postal}
                    onChange={(e) => setForm({...form, codigo_postal: e.target.value})}
                    placeholder="06600"
                    className="bg-stone-50 border-stone-200 rounded-xl h-12"
                    maxLength={5}
                    data-testid="billing-cp"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                    <Mail className="w-4 h-4" />
                    Email para Facturación
                  </label>
                  <Input
                    type="email"
                    value={form.email_facturacion}
                    onChange={(e) => setForm({...form, email_facturacion: e.target.value})}
                    placeholder="facturacion@empresa.com"
                    className="bg-stone-50 border-stone-200 rounded-xl h-12"
                    data-testid="billing-email"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-700 mb-2 block">Uso de CFDI</label>
                  <Select
                    value={form.uso_cfdi}
                    onValueChange={(value) => setForm({...form, uso_cfdi: value})}
                  >
                    <SelectTrigger className="bg-stone-50 border-stone-200 rounded-xl h-12" data-testid="billing-uso-cfdi">
                      <SelectValue placeholder="Seleccionar uso" />
                    </SelectTrigger>
                    <SelectContent>
                      {USO_CFDI_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                className="w-full bg-emerald-700 hover:bg-emerald-800 rounded-full py-6 mt-6"
                onClick={handleSaveBilling}
                disabled={loading}
                data-testid="save-billing-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Guardando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-5 h-5" />
                    Guardar Datos Fiscales
                  </span>
                )}
              </Button>
            </div>

            {/* Orders to Invoice */}
            {billingInfo && orders.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-orange-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-stone-900">Solicitar Factura</h2>
                </div>

                <div className="space-y-3">
                  {orders.map(order => (
                    <div
                      key={order.order_id}
                      className="flex items-center justify-between p-4 bg-stone-50 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-stone-900">
                          Pedido #{order.order_id.slice(-8).toUpperCase()}
                        </p>
                        <p className="text-sm text-stone-500">
                          {format(new Date(order.created_at), "dd MMM yyyy", { locale: es })} • ${order.total.toFixed(2)} MXN
                        </p>
                      </div>
                      {isInvoiceRequested(order.order_id) ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Solicitada
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => handleRequestInvoice(order.order_id)}
                          data-testid={`request-invoice-${order.order_id}`}
                        >
                          Solicitar Factura
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invoice Requests History */}
            {invoiceRequests.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <h2 className="text-lg font-semibold text-stone-900 mb-6">Historial de Facturas</h2>
                
                <div className="space-y-3">
                  {invoiceRequests.map(inv => {
                    const status = statusConfig[inv.status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    return (
                      <div
                        key={inv.request_id}
                        className="flex items-center justify-between p-4 border border-stone-100 rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${status.color}`}>
                            <StatusIcon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium text-stone-900">
                              Pedido #{inv.order_id.slice(-8).toUpperCase()}
                            </p>
                            <p className="text-sm text-stone-500">
                              {format(new Date(inv.created_at), "dd MMM yyyy HH:mm", { locale: es })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${status.color} border-0`}>
                            {status.label}
                          </Badge>
                          {inv.invoice_url && (
                            <Button size="sm" variant="ghost" className="rounded-full">
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
